import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Pencil, Trash2, Search, ShieldCheck } from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import type { IUser, IPaginatedResponse } from "@/types"

// ----------------------------------------------------------------------------

export default function Admins() {
  // Hooks
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<IUser | null>(null)
  const [deletingUser, setDeletingUser] = useState<IUser | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["admins", page, search],
    queryFn: () =>
      api.getUsers({ page, limit: 20, search }, token!) as Promise<IPaginatedResponse<IUser>>,
    enabled: !!token,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id, token!),
    onSuccess: () => {
      toast.success("Amministratore eliminato")
      queryClient.invalidateQueries({ queryKey: ["admins"] })
      setDeletingUser(null)
    },
    onError: () => toast.error("Errore durante l'eliminazione"),
  })

  const BACKOFFICE_ROLES = ["admin", "operator", "viewer"]
  const backofficeUsers = (data?.data || []).filter((u) => BACKOFFICE_ROLES.includes(u.role))
  const totalCount = data?.count || 0

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => { setEditingUser(null); setShowForm(true) }}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Nuovo Admin
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca per email o nome..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Utente</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Ruolo</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr><td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">Caricamento...</td></tr>
            ) : backofficeUsers.length === 0 ? (
              <tr><td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">Nessun utente backoffice trovato</td></tr>
            ) : (
              backofficeUsers.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                        {admin.displayName?.charAt(0)?.toUpperCase() || admin.email.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{admin.displayName || "-"}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{admin.email}</td>
                  <td className="px-6 py-4">
                    <RoleBadge role={admin.role} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setEditingUser(admin); setShowForm(true) }} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDeletingUser(admin)} className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {totalCount > 20 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-sm text-gray-500">{totalCount} risultati</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-50">Precedente</button>
              <button onClick={() => setPage((p) => p + 1)} disabled={page * 20 >= totalCount} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-50">Successivo</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      {showForm && (
        <AdminFormDialog
          admin={editingUser}
          token={token!}
          onClose={() => { setShowForm(false); setEditingUser(null) }}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ["admins"] }); setShowForm(false); setEditingUser(null) }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Conferma eliminazione</h3>
            <p className="mt-2 text-sm text-gray-500">Eliminare <strong>{deletingUser.displayName || deletingUser.email}</strong>?</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDeletingUser(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
              <button onClick={() => deleteMutation.mutate(deletingUser.id)} disabled={deleteMutation.isPending} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deleteMutation.isPending ? "Eliminazione..." : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================

const ROLE_OPTIONS = [
  { value: "admin", label: "Amministratore", description: "Accesso completo a tutte le funzionalità" },
  { value: "operator", label: "Operatore", description: "Può visualizzare clienti e movimentare punti" },
  { value: "viewer", label: "Visualizzatore", description: "Sola consultazione, nessuna modifica" },
] as const

const ROLE_STYLES: Record<string, string> = {
  admin: "bg-primary/10 text-primary",
  operator: "bg-amber-100 text-amber-700",
  viewer: "bg-gray-100 text-gray-600",
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Amministratore",
  operator: "Operatore",
  viewer: "Visualizzatore",
}

function RoleBadge({ role }: { role: string }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_STYLES[role] || "bg-gray-100 text-gray-600"}`}>
      <ShieldCheck className="h-3 w-3" />{ROLE_LABELS[role] || role}
    </span>
  )
}

function AdminFormDialog({ admin, token, onClose, onSuccess }: { admin: IUser | null; token: string; onClose: () => void; onSuccess: () => void }) {
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState(admin?.email || "")
  const [displayName, setDisplayName] = useState(admin?.displayName || "")
  const [surname, setSurname] = useState("")
  const [role, setRole] = useState(admin?.role || "operator")
  const [password, setPassword] = useState("")
  const isEditing = !!admin

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      if (isEditing) {
        const body: Record<string, unknown> = { displayName, role }
        if (password) body.password = password
        await api.updateUser(admin.id, body, token)
        toast.success("Utente aggiornato")
      } else {
        const fullDisplayName = [displayName, surname].filter(Boolean).join(" ")
        await api.createUser({ email, displayName: fullDisplayName, password, role }, token)
        toast.success("Utente creato")
      }
      onSuccess()
    } catch { toast.error(isEditing ? "Errore nell'aggiornamento" : "Errore nella creazione") }
    finally { setIsLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{isEditing ? "Modifica Utente" : "Nuovo Utente Backoffice"}</h3>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isEditing} required className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-gray-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Nome</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Cognome</label>
              <input type="text" value={surname} onChange={(e) => setSurname(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Ruolo</label>
            <div className="space-y-2">
              {ROLE_OPTIONS.map((opt) => (
                <label key={opt.value} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${role === opt.value ? "border-primary bg-primary/5" : "border-gray-200 hover:bg-gray-50"}`}>
                  <input type="radio" name="role" value={opt.value} checked={role === opt.value} onChange={() => setRole(opt.value)} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">{isEditing ? "Nuova password (opzionale)" : "Password"}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required={!isEditing} minLength={8} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
            <button type="submit" disabled={isLoading} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {isLoading ? "Salvataggio..." : isEditing ? "Salva" : "Crea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
